import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react'
import en from './locales/en.json'
import zh from './locales/zh.json'

export type SupportedLanguage = 'en' | 'zh'

interface I18nContextValue {
  language: SupportedLanguage
  setLanguage: (lang: SupportedLanguage) => void
  t: (key: string, vars?: Record<string, string | number | undefined | null>) => string
}

const I18N_RESOURCES: Record<SupportedLanguage, any> = { en, zh }

const I18nContext = createContext<I18nContextValue | null>(null)

function getInitialLanguage(): SupportedLanguage {
  try {
    const saved = localStorage.getItem('lang') as SupportedLanguage | null
    if (saved === 'en' || saved === 'zh') return saved
  } catch {}
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language.toLowerCase()
    if (nav.startsWith('zh')) return 'zh'
  }
  return 'en'
}

function deepGet(obj: any, path: string): unknown {
  return path.split('.').reduce((acc: any, key: string) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj)
}

function interpolate(template: string, vars?: Record<string, string | number | undefined | null>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = vars[k]
    if (v === undefined || v === null) return ''
    return String(v)
  })
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage())

  useEffect(() => {
    try {
      localStorage.setItem('lang', language)
    } catch {}
  }, [language])

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang)
  }, [])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number | undefined | null>): string => {
      const res = deepGet(I18N_RESOURCES[language], key)
      if (typeof res === 'string') return interpolate(res, vars)
      // fallback to English
      const fallback = deepGet(I18N_RESOURCES.en, key)
      if (typeof fallback === 'string') return interpolate(fallback, vars)
      return key
    },
    [language]
  )

  const value = useMemo<I18nContextValue>(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
